import type { ZoneTreeNode } from "@gettactic/api";
import {
  useInvalidateZones,
  useTacticHooks,
  useZonesTreeQuery
} from "@gettactic/hooks";
import React from "react";
import { ZoneShape, ZoneTypeDefinition } from "../../../../types";
import { isOrganisationZoneId } from "../../../../utils/zones";
import { ColorPickerInput } from "../../../ui/ColorPickerInput";
import { EditorField } from "../../../ui/EditorField";
import { EditorSelect } from "../../../ui/EditorSelect";
import { EditorSectionLabel, PanelSection } from "../../../ui/PanelShell";
import { useMapCanvas } from "../../contexts/MapCanvasContext";

const NO_ZONE_VALUE = "__none__";

export interface ZoneAssignmentSectionProps {
  shape: ZoneShape;
  /** Tag / untag the selected zone shapes with an organisation zone. */
  onChangeZone: (zone: ZoneTypeDefinition | undefined) => void;
}

interface FlatZoneOption {
  value: string;
  label: string;
  node: ZoneTreeNode;
}

const flattenTree = (nodes: ZoneTreeNode[], depth = 0): FlatZoneOption[] =>
  nodes.flatMap((node) => [
    {
      value: node.id,
      label: `${"— ".repeat(depth)}${node.name}`,
      node
    },
    ...flattenTree(node.children ?? [], depth + 1)
  ]);

/**
 * "Zone" section of the zone inspector: tags the region with one of the
 * organisation's zones (the tree managed in Settings), with an inline
 * "new zone" form. Selecting a zone also caches its name and colour in the
 * map's `zoneTypes` registry so the canvas paints it right away.
 */
export const ZoneAssignmentSection: React.FC<ZoneAssignmentSectionProps> = ({
  shape,
  onChangeZone
}) => {
  const { apiClient } = useTacticHooks();
  const { data: tree, isLoading } = useZonesTreeQuery();
  const invalidateZones = useInvalidateZones();
  const { setZoneTypes } = useMapCanvas();

  const [isAdding, setIsAdding] = React.useState(false);
  const [newName, setNewName] = React.useState("");
  const [newColor, setNewColor] = React.useState("#3B82F6");
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const options = React.useMemo(() => flattenTree(tree ?? []), [tree]);
  const selectedZoneId = shape.zone_id ?? shape.categoryTypeId;
  const selected = options.find((option) => option.value === selectedZoneId);
  const isLegacyCategory =
    !!shape.categoryTypeId &&
    !shape.zone_id &&
    !isOrganisationZoneId(shape.categoryTypeId);

  const cacheZone = (zone: ZoneTypeDefinition) => {
    setZoneTypes((prev) => {
      const exists = prev.some((entry) => entry.id === zone.id);
      return exists
        ? prev.map((entry) => (entry.id === zone.id ? zone : entry))
        : [...prev, zone];
    });
  };

  const pickZone = (zoneId: string) => {
    if (zoneId === NO_ZONE_VALUE) {
      onChangeZone(undefined);
      return;
    }
    const option = options.find((item) => item.value === zoneId);
    if (!option) return;
    const definition: ZoneTypeDefinition = {
      id: option.node.id,
      name: option.node.name,
      color: option.node.color,
      icon: option.node.icon ?? undefined
    };
    cacheZone(definition);
    onChangeZone(definition);
  };

  const commitNewZone = async () => {
    const trimmed = newName.trim();
    if (!trimmed || isSaving) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const response = await apiClient.zones.create({
        name: trimmed,
        color: newColor
      });
      const created = response.result;
      if (!created) {
        setSaveError("The zone could not be created");
        return;
      }
      const definition: ZoneTypeDefinition = {
        id: created.id,
        name: created.name,
        color: created.color,
        icon: created.icon ?? undefined
      };
      cacheZone(definition);
      onChangeZone(definition);
      await invalidateZones();
      setNewName("");
      setNewColor("#3B82F6");
      setIsAdding(false);
    } catch (error) {
      setSaveError(
        error instanceof Error && error.message
          ? error.message
          : "The zone could not be created"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const otherOffices = selected
    ? Math.max(selected.node.office_ids.length - 1, 0)
    : 0;

  return (
    <PanelSection>
      <div className="flex items-baseline justify-between">
        <EditorSectionLabel>Zone</EditorSectionLabel>
        <button
          type="button"
          className="text-blue-600 text-xs hover:text-blue-700"
          onClick={() => setIsAdding((value) => !value)}
        >
          {isAdding ? "Cancel" : "+ New"}
        </button>
      </div>
      <div className="flex flex-col gap-1">
        <EditorSelect
          label="Zone"
          value={selected ? selected.value : NO_ZONE_VALUE}
          onValueChange={pickZone}
          options={[
            {
              value: NO_ZONE_VALUE,
              label: isLoading ? "Loading zones…" : "(no zone)"
            },
            ...options.map(({ value, label }) => ({ value, label }))
          ]}
        />
        {selected ? (
          <p className="flex items-center gap-1.5 text-[11px] text-neutral-500">
            <span
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm border border-neutral-300"
              style={{ background: selected.node.color }}
            />
            <span className="truncate">
              {selected.node.desks} desk{selected.node.desks === 1 ? "" : "s"}{" "}
              across {selected.node.regions} region
              {selected.node.regions === 1 ? "" : "s"}
              {otherOffices > 0
                ? ` · also in ${otherOffices} other office${otherOffices === 1 ? "" : "s"}`
                : ""}
            </span>
          </p>
        ) : (
          <p className="text-[11px] text-neutral-400">
            Zones are organisation-wide allocations (Engineering, Quiet zone).
            Rooms and corridors do not need one.
          </p>
        )}
        {isLegacyCategory ? (
          <p className="rounded-md bg-amber-50 px-2 py-1.5 text-[11px] text-amber-700">
            This zone uses a map-local category. It will become an organisation
            zone with the same name when you save.
          </p>
        ) : null}
        {isAdding && (
          <div className="mt-1 flex flex-col gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-2">
            <EditorField
              label="Zone name"
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Zone name (e.g. Engineering)"
            />
            <div className="flex items-center gap-2">
              <ColorPickerInput value={newColor} onChange={setNewColor} />
              <button
                type="button"
                className="ml-auto flex h-7 items-center justify-center rounded-md bg-blue-600 px-3 font-medium text-white text-xs shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-neutral-300"
                onClick={commitNewZone}
                disabled={!newName.trim() || isSaving}
              >
                {isSaving ? "Adding…" : "Add"}
              </button>
            </div>
            {saveError ? (
              <p className="text-[11px] text-red-600">{saveError}</p>
            ) : null}
          </div>
        )}
      </div>
    </PanelSection>
  );
};
